import { createContext, useContext, useState, useCallback, useMemo } from 'react';

const translations = {
  en: {
    nav: { list: 'List', calendar: 'Calendar', kanban: 'Kanban', analytics: 'Analytics', settings: 'Settings', circle: 'Circle' },
    circle: { tasks: 'tasks' },
    task: {
      add: 'Add Task', quickAdd: 'Quick add task...', edit: 'Edit Task', delete: 'Delete', complete: 'Complete',
      title: 'Title', description: 'Description', priority: 'Priority', tags: 'Tags', list: 'List',
      status: 'Status', subtasks: 'Subtasks', dueDate: 'Due Date', recurrence: 'Recurrence',
      save: 'Save', cancel: 'Cancel', noTasks: 'No tasks yet', addFirst: 'Add your first task to get started',
      none: 'None', low: 'Low', medium: 'Medium', high: 'High', urgent: 'Urgent',
      addSubtask: 'Add subtask', deleteConfirm: 'Delete this task?', completed: 'Completed',
      active: 'Active', completedTasks: 'Completed', restore: 'Restore',
      overdue: 'Overdue', today: 'Today', tomorrow: 'Tomorrow', thisWeek: 'This Week', thisMonth: 'This Month', later: 'Later', noDate: 'No Date',
      boards: 'Boards', addToBoard: 'Add to board...',
      duration: 'Duration', minutes: 'min', hours: 'hr', days: 'day', filterTags: 'Filter tags...',
      reference: 'Not a task', referenceHint: 'Reference item — excluded from analytics',
      select: 'Select', deleted: 'Deleted', repeats: 'Repeat',
      noOverdue: 'No overdue tasks', noDeleted: 'No deleted tasks', noTasksFound: 'No tasks found',
      deleteRecurringTitle: 'Delete repeating task?',
      deleteRecurringDesc: 'This task repeats. Remove just this occurrence, or delete the entire series?',
      deleteOccurrence: 'Only this occurrence', deleteSeries: 'Delete entire series',
      movedToTrash: 'Moved to trash', undo: 'Undo',
    },
    bulk: {
      selected: '{count} selected', setDate: 'Set Date', removeDate: 'Remove Date',
      plus1Day: '+1 Day', plus1Week: '+1 Week', plus1Month: '+1 Month', plus1Year: '+1 Year',
    },
    calendar: {
      today: 'Today', day: 'Day', threeDay: '3 Day', week: 'Week', twoWeek: '2 Week', month: 'Month',
      year: 'Year', next365: '365 Days',
      noEvents: 'No events', more: '+{count} more', rescheduled: 'Rescheduled',
      allLists: 'All lists',
    },
    kanban: {
      addColumn: 'Add Column', editColumn: 'Edit Column', deleteColumn: 'Delete Column',
      columnTitle: 'Column title', noTasks: 'No tasks',
      newBoard: 'New Board', editBoard: 'Edit Board', deleteBoard: 'Delete Board', newColumn: 'New Column',
      boardName: 'Board name', columnName: 'Column name', searchTasks: 'Search tasks...',
      addTaskToColumn: 'Add Task to Column', createBoardPrompt: 'Create a board to get started', selectBoard: 'Select a board',
      doneColumnHint: 'Tasks dropped here are marked done',
    },
    analytics: {
      title: 'Analytics', created: 'Created', completed: 'Completed', totalTasks: 'Total Tasks',
      totalCompleted: 'Total Completed', pending: 'Pending', upcomingWeek: 'Upcoming (7d)',
      daily: 'Daily Activity', last30: 'Last 30 days', completionTrend: 'Completion Trend',
    },
    settings: {
      title: 'Settings', language: 'Language', theme: 'Theme', devices: 'Devices',
      addDevice: 'Add Device', deleteDevice: 'Delete Device', deviceName: 'Device Name',
      light: 'Light', dark: 'Dark', auto: 'Auto', english: 'English', korean: 'Korean',
      currentDevice: 'Current', tokenWarning: 'Store this token securely. It will not be shown again.',
      createdAt: 'Created', lastSeen: 'Last seen',
      backup: 'Backup', export: 'Export', import: 'Import',
      exportDesc: 'Download all data as a JSON file', importDesc: 'Restore data from a backup file',
      importWarning: 'Warning: importing will replace all existing data.',
      exportMd: 'Export Markdown', exportMdDesc: 'Download this workspace as a Markdown checklist',
      importMd: 'Import Markdown', importMdDesc: 'Add tasks from a Markdown checklist (does not replace existing data)',
      todoist: 'Todoist', todoistDesc: 'Move tasks to or from a Todoist account. Running an export or import again updates what is already there instead of duplicating it.',
      todoistToken: 'Personal API token', todoistTokenHint: 'Todoist → Settings → Integrations → Developer → API token. Kept on the server and shown only by its last 4 characters.',
      todoistConnect: 'Connect', todoistDisconnect: 'Disconnect', todoistDisconnectDesc: 'Removes the stored token. Nothing in Todoist or Tododuk is changed.',
      todoistConnected: 'Connected · token ending {last4}',
      todoistIncludeCompleted: 'Include tasks completed in the last 30 days',
      todoistExport: 'Export this workspace to Todoist', todoistExportDesc: 'Lists become projects under a "{ws}" project, tags become labels, subtasks stay subtasks. Only lists you can see are sent.',
      todoistImport: 'Import from Todoist', todoistImportDesc: 'Projects become lists in this workspace (you own them), labels become tags.',
      todoistLoadProjects: 'Choose projects…', todoistRun: 'Go',
      todoistPreviewExport: '{tasks} tasks and {subtasks} subtasks from {lists} lists, {tags} tags{completed}. Not exported: {extra_lists} extra list memberships, {assignees} assignees, {count_rules} repeat counts.',
      todoistPreviewImport: '{tasks} tasks and {subtasks} subtasks from {projects} projects, {labels} labels, {completed} completed. {unparsed} repeat rules Todoist-only (kept as a note on the task).',
      todoistRunning: 'Working… {done}/{total}', todoistDone: 'Done · {created} created, {updated} updated', todoistFailed: 'Failed · {error}', todoistWarnings: '{n} notes',
      todoistLastExport: 'Last export {when}', todoistLastImport: 'Last import {when}',
      todoistCsv: 'CSV files (no account needed)', todoistCsvDesc: 'One Todoist project per file. Download a list as a Todoist template, or import a Todoist CSV export as a new list.',
      todoistCsvExport: 'Download CSV', todoistCsvImport: 'Import CSV', todoistSelectList: 'Select a list',
      defaultList: 'Default List', defaultListDesc: 'New tasks go here when adding from "All" view',
      importanceMode: 'Priority Display', flagMode: 'Flag Icon', backgroundMode: 'Color Background',
      timeBlocks: 'Time Grouping', next7Days: 'Show individual days', thisWeek: 'Show "This Week"', thisMonth: 'Show "This Month"',
      completionEffects: 'Completion Effects', animation: 'Confetti animation', sound: 'Sound effect',
      tagsVisibility: 'Tags Visibility', showTags: 'Show tags on tasks',
      users: 'Users', addUser: 'Add User', deviceAssignments: 'Device Assignments',
      shareList: 'Share List', sharedWith: 'Shared with', unshare: 'Unshare', share: 'Share',
      selectUsers: 'Select users to share with', noUsers: 'No other users to share with',
      assignTo: 'Assign to', unassigned: 'Unassigned',
      userEmailPlaceholder: 'email for task intake', userTelegramPlaceholder: 'Telegram chat ID', emailSaved: 'Saved',
      newWorkspace: 'New Workspace', editWorkspace: 'Edit Workspace', workspaceName: 'Workspace name',
      newList: 'New List', editList: 'Edit List', listName: 'List name',
      newTag: 'New Tag', tagName: 'Tag name', noTags: 'No tags',
      userName: 'User name', noUsersYet: 'No users yet', active: 'Active', setAsDefault: 'Set as default',
      workspaceHint: 'Each workspace has its own lists, tags, and boards. Switch from the sidebar.',
      showOverdueTab: 'Show overdue tab', showDeletedTab: 'Show deleted tab',
      workspaces: 'Workspaces', lists: 'Lists', listView: 'List View', defaultBoard: 'Default Board',
      default: 'Default', shared: 'Shared', color: 'Color',
      reference: 'Reference', referenceWorkspace: 'Reference workspace',
      referenceWorkspaceHint: 'Items here are notes, not to-dos — excluded from analytics and counts',
      sharedWorkspace: 'Shared workspace',
      sharedWorkspaceHint: 'Visible to everyone — otherwise only you can see it',
    },
    auth: {
      setup: 'Setup', welcome: 'Welcome to Tododuk', setupDesc: 'Register your first device to get started.',
      deviceName: 'Device Name', register: 'Register Device', enterToken: 'Enter Token',
      tokenDesc: 'Enter the device token to access the system.',
      token: 'Device Token', connect: 'Connect', haveToken: 'Already have a token?', pasteToken: 'Paste your token here',
      needSetup: 'Need to set up a new device?', tokenSaved: 'Token saved! Store it securely.',
    },
    common: {
      save: 'Save', cancel: 'Cancel', delete: 'Delete', confirm: 'Confirm', search: 'Search...',
      loading: 'Loading...', error: 'Error', success: 'Success', close: 'Close', all: 'All', add: 'Add',
    },
    invite: {
      button: 'Invite a device',
      forUser: 'Invite a device for {name}',
      title: 'Add a device for {name}',
      instructions: 'Scan this with the new phone’s camera, or open the app there and type the code below.',
      orTypeCode: 'or type this code',
      link: 'Invite link', copyLink: 'Copy invite link',
      copyFailed: 'Could not copy — select the link and copy it manually.',
      expiry: 'This code works once and expires in 15 minutes.',
      joinTitle: 'Join with a code',
      joinDesc: 'Enter the invite code from someone who already uses Tododuk.',
      codeLabel: 'Invite code',
      join: 'Join',
      welcome: 'Welcome, {name}!',
      redeemFailed: 'That code did not work. Ask for a fresh one.',
      haveTokenInstead: 'I have a device token instead',
      haveCodeInstead: 'I have an invite code instead',
    },
    error: {
      title: 'Something went wrong',
      description: 'The app hit an unexpected error. Your tasks are safe on the server — nothing was lost.',
      retry: 'Try again', reload: 'Reload',
    },
    confirm: {
      typeToConfirm: 'Type {phrase} to confirm',
      ownerOnly: 'Only the account owner can do this.',
      deleteUserTitle: 'Delete {name}?',
      deleteUserDesc: 'Their devices will be signed out and their personal settings removed. Tasks and lists are not deleted.',
      deleteDeviceTitle: 'Remove {name}?',
      deleteDeviceDesc: 'That device is signed out immediately and must be set up again with a new token.',
      deleteListTitle: 'Delete list {name}?',
      deleteListDesc: 'The list is removed and its tasks are unfiled. The tasks themselves are kept.',
      deleteTagTitle: 'Delete tag {name}?',
      deleteTagDesc: 'The tag is removed from every task that uses it.',
      deleteWorkspaceTitle: 'Delete workspace {name}?',
      deleteWorkspaceDesc: 'Only empty workspaces can be deleted.',
      unshareTitle: 'Stop sharing with {name}?',
      unshareDesc: 'They immediately lose access to this list and its tasks.',
      importTitle: 'Replace ALL data with this backup?',
      importDesc: 'Every task, list, workspace and setting for everyone is deleted and replaced. This affects the whole family, not just you.',
      importPhrase: 'REPLACE',
      importMergeTitle: 'Merge this backup in?',
      importMergeDesc: 'Items in the file are added or updated. Nothing is deleted.',
      importMode: 'How should this backup be applied?',
      importMerge: 'Merge (safe)', importReplace: 'Replace everything',
    },
    recurrence: {
      none: 'No repeat', daily: 'Daily', weekly: 'Weekly', monthly: 'Monthly', yearly: 'Yearly',
      every: 'Every', days: 'days', weeks: 'weeks', months: 'months', years: 'years',
      ends: 'Ends', never: 'Never', onDate: 'On date', afterOccurrences: 'After occurrences',
      on: 'On', repeatUntil: 'Repeat until (optional)', noEndDate: 'No end date',
    },
    days: { MO: 'Mon', TU: 'Tue', WE: 'Wed', TH: 'Thu', FR: 'Fri', SA: 'Sat', SU: 'Sun' },
  },
  ko: {
    nav: { list: '목록', calendar: '캘린더', kanban: '칸반', analytics: '분석', settings: '설정', circle: '원형' },
    circle: { tasks: '개 작업' },
    task: {
      add: '작업 추가', quickAdd: '빠른 작업 추가...', edit: '작업 편집', delete: '삭제', complete: '완료',
      title: '제목', description: '설명', priority: '우선순위', tags: '태그', list: '목록',
      status: '상태', subtasks: '하위 작업', dueDate: '마감일', recurrence: '반복',
      save: '저장', cancel: '취소', noTasks: '작업이 없습니다', addFirst: '첫 번째 작업을 추가하세요',
      none: '없음', low: '낮음', medium: '보통', high: '높음', urgent: '긴급',
      addSubtask: '하위 작업 추가', deleteConfirm: '이 작업을 삭제할까요?', completed: '완료됨',
      active: '활성', completedTasks: '완료됨', restore: '복원',
      overdue: '기한 초과', today: '오늘', tomorrow: '내일', thisWeek: '이번 주', thisMonth: '이번 달', later: '이후', noDate: '날짜 없음',
      boards: '보드', addToBoard: '보드에 추가...',
      duration: '소요 시간', minutes: '분', hours: '시간', days: '일', filterTags: '태그 필터...',
      reference: '할 일 아님', referenceHint: '참고용 항목 — 분석에서 제외됩니다',
      select: '선택', deleted: '삭제됨', repeats: '반복',
      noOverdue: '기한 초과 작업 없음', noDeleted: '삭제된 작업 없음', noTasksFound: '작업을 찾을 수 없습니다',
      deleteRecurringTitle: '반복 작업을 삭제할까요?',
      deleteRecurringDesc: '반복되는 작업입니다. 이번 회차만 삭제할까요, 아니면 전체 반복을 삭제할까요?',
      deleteOccurrence: '이번 회차만', deleteSeries: '전체 반복 삭제',
      movedToTrash: '휴지통으로 이동됨', undo: '실행 취소',
    },
    bulk: {
      selected: '{count}개 선택됨', setDate: '날짜 설정', removeDate: '날짜 제거',
      plus1Day: '+1일', plus1Week: '+1주', plus1Month: '+1개월', plus1Year: '+1년',
    },
    calendar: {
      today: '오늘', day: '일', threeDay: '3일', week: '주', twoWeek: '2주', month: '월',
      year: '연간', next365: '365일',
      noEvents: '이벤트 없음', more: '+{count}개 더', rescheduled: '일정 변경됨',
      allLists: '모든 목록',
    },
    kanban: {
      addColumn: '열 추가', editColumn: '열 편집', deleteColumn: '열 삭제',
      columnTitle: '열 제목', noTasks: '작업 없음',
      newBoard: '새 보드', editBoard: '보드 편집', deleteBoard: '보드 삭제', newColumn: '새 열',
      boardName: '보드 이름', columnName: '열 이름', searchTasks: '작업 검색...',
      addTaskToColumn: '열에 작업 추가', createBoardPrompt: '시작하려면 보드를 만드세요', selectBoard: '보드를 선택하세요',
      doneColumnHint: '여기에 놓인 작업은 완료로 표시됩니다',
    },
    analytics: {
      title: '분석', created: '생성', completed: '완료', totalTasks: '전체 작업',
      totalCompleted: '완료된 작업', pending: '대기 중', upcomingWeek: '다가오는 (7일)',
      daily: '일별 활동', last30: '최근 30일', completionTrend: '완료 추이',
    },
    settings: {
      title: '설정', language: '언어', theme: '테마', devices: '기기',
      addDevice: '기기 추가', deleteDevice: '기기 삭제', deviceName: '기기 이름',
      light: '라이트', dark: '다크', auto: '자동', english: '영어', korean: '한국어',
      currentDevice: '현재', tokenWarning: '이 토큰을 안전하게 보관하세요. 다시 표시되지 않습니다.',
      createdAt: '생성일', lastSeen: '마지막 접속',
      backup: '백업', export: '내보내기', import: '가져오기',
      exportDesc: '모든 데이터를 JSON 파일로 내보내기', importDesc: '백업 파일에서 데이터 복원',
      importWarning: '경고: 가져오기는 기존 데이터를 대체합니다.',
      exportMd: '마크다운 내보내기', exportMdDesc: '이 워크스페이스를 마크다운 체크리스트로 내보내기',
      importMd: '마크다운 가져오기', importMdDesc: '마크다운 체크리스트에서 작업 추가 (기존 데이터 유지)',
      todoist: 'Todoist', todoistDesc: 'Todoist 계정과 작업을 주고받습니다. 다시 실행하면 중복 생성 없이 기존 항목을 업데이트합니다.',
      todoistToken: '개인 API 토큰', todoistTokenHint: 'Todoist → 설정 → 연동 → 개발자 → API 토큰. 서버에 저장되며 마지막 4자리만 표시됩니다.',
      todoistConnect: '연결', todoistDisconnect: '연결 해제', todoistDisconnectDesc: '저장된 토큰을 삭제합니다. Todoist나 Tododuk의 데이터는 변경되지 않습니다.',
      todoistConnected: '연결됨 · 토큰 끝자리 {last4}',
      todoistIncludeCompleted: '최근 30일 내 완료된 작업 포함',
      todoistExport: '이 워크스페이스를 Todoist로 내보내기', todoistExportDesc: '목록은 "{ws}" 프로젝트 아래의 프로젝트로, 태그는 라벨로, 하위 작업은 하위 작업으로 보냅니다. 내가 볼 수 있는 목록만 전송됩니다.',
      todoistImport: 'Todoist에서 가져오기', todoistImportDesc: '프로젝트는 이 워크스페이스의 목록(내 소유)으로, 라벨은 태그로 가져옵니다.',
      todoistLoadProjects: '프로젝트 선택…', todoistRun: '실행',
      todoistPreviewExport: '{lists}개 목록의 작업 {tasks}개, 하위 작업 {subtasks}개, 태그 {tags}개{completed}. 제외: 추가 목록 소속 {extra_lists}건, 담당자 {assignees}건, 반복 횟수 {count_rules}건.',
      todoistPreviewImport: '{projects}개 프로젝트의 작업 {tasks}개, 하위 작업 {subtasks}개, 라벨 {labels}개, 완료 {completed}개. Todoist 전용 반복 규칙 {unparsed}건은 메모로 보관됩니다.',
      todoistRunning: '진행 중… {done}/{total}', todoistDone: '완료 · 생성 {created}, 업데이트 {updated}', todoistFailed: '실패 · {error}', todoistWarnings: '참고 {n}건',
      todoistLastExport: '마지막 내보내기 {when}', todoistLastImport: '마지막 가져오기 {when}',
      todoistCsv: 'CSV 파일 (계정 불필요)', todoistCsvDesc: '파일 하나가 Todoist 프로젝트 하나입니다. 목록을 Todoist 템플릿으로 다운로드하거나, Todoist CSV 내보내기 파일을 새 목록으로 가져옵니다.',
      todoistCsvExport: 'CSV 다운로드', todoistCsvImport: 'CSV 가져오기', todoistSelectList: '목록 선택',
      defaultList: '기본 목록', defaultListDesc: '"전체" 보기에서 추가할 때 여기에 저장됩니다',
      importanceMode: '우선순위 표시', flagMode: '깃발 아이콘', backgroundMode: '배경 색상',
      timeBlocks: '시간 그룹', next7Days: '개별 요일 표시', thisWeek: '"이번 주" 표시', thisMonth: '"이번 달" 표시',
      completionEffects: '완료 효과', animation: '폭죽 애니메이션', sound: '사운드 효과',
      tagsVisibility: '태그 표시', showTags: '작업에 태그 표시',
      users: '사용자', addUser: '사용자 추가', deviceAssignments: '기기 할당',
      shareList: '목록 공유', sharedWith: '공유 대상', unshare: '공유 해제', share: '공유',
      selectUsers: '공유할 사용자를 선택하세요', noUsers: '공유할 다른 사용자가 없습니다',
      assignTo: '담당자', unassigned: '미할당',
      userEmailPlaceholder: '작업 추가용 이메일', userTelegramPlaceholder: '텔레그램 채팅 ID', emailSaved: '저장됨',
      newWorkspace: '새 워크스페이스', editWorkspace: '워크스페이스 편집', workspaceName: '워크스페이스 이름',
      newList: '새 목록', editList: '목록 편집', listName: '목록 이름',
      newTag: '새 태그', tagName: '태그 이름', noTags: '태그 없음',
      userName: '사용자 이름', noUsersYet: '사용자가 없습니다', active: '활성', setAsDefault: '기본으로 설정',
      workspaceHint: '각 워크스페이스는 자체 목록, 태그, 보드를 가집니다. 사이드바에서 전환하세요.',
      showOverdueTab: '기한 초과 탭 표시', showDeletedTab: '삭제됨 탭 표시',
      workspaces: '워크스페이스', lists: '목록', listView: '목록 보기', defaultBoard: '기본 보드',
      default: '기본', shared: '공유됨', color: '색상',
      reference: '참고용', referenceWorkspace: '참고용 워크스페이스',
      referenceWorkspaceHint: '여기 항목은 할 일이 아닌 메모로, 분석과 카운트에서 제외됩니다',
      sharedWorkspace: '공유 워크스페이스',
      sharedWorkspaceHint: '모든 사용자에게 표시됩니다 — 끄면 본인만 볼 수 있습니다',
    },
    auth: {
      setup: '설정', welcome: 'Tododuk 투두둑에 오신 것을 환영합니다', setupDesc: '시작하려면 첫 번째 기기를 등록하세요.',
      deviceName: '기기 이름', register: '기기 등록', enterToken: '토큰 입력',
      tokenDesc: '시스템에 접근하려면 기기 토큰을 입력하세요.',
      token: '기기 토큰', connect: '연결', haveToken: '이미 토큰이 있으신가요?', pasteToken: '토큰을 여기에 붙여넣으세요',
      needSetup: '새 기기를 설정해야 하나요?', tokenSaved: '토큰이 저장되었습니다! 안전하게 보관하세요.',
    },
    common: {
      save: '저장', cancel: '취소', delete: '삭제', confirm: '확인', search: '검색...',
      loading: '로딩 중...', error: '오류', success: '성공', close: '닫기', all: '전체', add: '추가',
    },
    invite: {
      button: '기기 초대',
      forUser: '{name} 님의 기기 초대',
      title: '{name} 님의 기기 추가',
      instructions: '새 휴대폰 카메라로 스캔하거나, 그 기기에서 앱을 열고 아래 코드를 입력하세요.',
      orTypeCode: '또는 이 코드를 입력하세요',
      link: '초대 링크', copyLink: '초대 링크 복사',
      copyFailed: '복사할 수 없습니다 — 링크를 직접 선택해 복사하세요.',
      expiry: '이 코드는 한 번만 사용할 수 있으며 15분 후 만료됩니다.',
      joinTitle: '코드로 참여하기',
      joinDesc: '이미 Tododuk을 사용 중인 사람에게 받은 초대 코드를 입력하세요.',
      codeLabel: '초대 코드',
      join: '참여',
      welcome: '{name} 님, 환영합니다!',
      redeemFailed: '코드가 작동하지 않았습니다. 새 코드를 요청하세요.',
      haveTokenInstead: '기기 토큰이 있습니다',
      haveCodeInstead: '초대 코드가 있습니다',
    },
    error: {
      title: '문제가 발생했습니다',
      description: '예기치 않은 오류가 발생했습니다. 작업은 서버에 안전하게 저장되어 있으며 손실된 것은 없습니다.',
      retry: '다시 시도', reload: '새로고침',
    },
    confirm: {
      typeToConfirm: '확인하려면 {phrase} 를 입력하세요',
      ownerOnly: '계정 소유자만 할 수 있습니다.',
      deleteUserTitle: '{name} 님을 삭제할까요?',
      deleteUserDesc: '해당 사용자의 기기가 로그아웃되고 개인 설정이 삭제됩니다. 작업과 목록은 삭제되지 않습니다.',
      deleteDeviceTitle: '{name} 기기를 삭제할까요?',
      deleteDeviceDesc: '해당 기기는 즉시 로그아웃되며 새 토큰으로 다시 설정해야 합니다.',
      deleteListTitle: '{name} 목록을 삭제할까요?',
      deleteListDesc: '목록이 삭제되고 해당 작업은 목록에서 분리됩니다. 작업 자체는 유지됩니다.',
      deleteTagTitle: '{name} 태그를 삭제할까요?',
      deleteTagDesc: '이 태그를 사용하는 모든 작업에서 태그가 제거됩니다.',
      deleteWorkspaceTitle: '{name} 워크스페이스를 삭제할까요?',
      deleteWorkspaceDesc: '비어 있는 워크스페이스만 삭제할 수 있습니다.',
      unshareTitle: '{name} 님과의 공유를 중단할까요?',
      unshareDesc: '이 목록과 작업에 대한 접근 권한이 즉시 사라집니다.',
      importTitle: '모든 데이터를 이 백업으로 교체할까요?',
      importDesc: '모든 사람의 작업, 목록, 워크스페이스, 설정이 삭제되고 교체됩니다. 본인뿐 아니라 가족 전체에 영향을 줍니다.',
      importPhrase: 'REPLACE',
      importMergeTitle: '이 백업을 병합할까요?',
      importMergeDesc: '파일의 항목이 추가되거나 업데이트됩니다. 삭제되는 항목은 없습니다.',
      importMode: '이 백업을 어떻게 적용할까요?',
      importMerge: '병합 (안전)', importReplace: '전체 교체',
    },
    recurrence: {
      none: '반복 안 함', daily: '매일', weekly: '매주', monthly: '매월', yearly: '매년',
      every: '매', days: '일', weeks: '주', months: '개월', years: '년',
      ends: '종료', never: '없음', onDate: '날짜', afterOccurrences: '회 후',
      on: '에', repeatUntil: '반복 종료일 (선택)', noEndDate: '종료일 없음',
    },
    days: { MO: '월', TU: '화', WE: '수', TH: '목', FR: '금', SA: '토', SU: '일' },
  },
};

const I18nContext = createContext(null);

export function I18nProvider({ children }) {
  const [lang, setLang] = useState(() => localStorage.getItem('taskflow_lang') || 'en');

  const changeLang = useCallback((newLang) => {
    setLang(newLang);
    localStorage.setItem('taskflow_lang', newLang);
    // Mirror to backend settings so the intake bot (Telegram/email/Signal)
    // replies and alerts in the same language. Fire-and-forget: may run
    // before auth, in which case the server-side default stays.
    import('@/lib/api').then(({ default: api }) => api.put('/settings', { language: newLang })).catch(() => {});
  }, []);

  const t = useCallback((key, params) => {
    const keys = key.split('.');
    let val = translations[lang];
    for (const k of keys) {
      if (!val) return key;
      val = val[k];
    }
    if (typeof val === 'string' && params) {
      return Object.entries(params).reduce((s, [k, v]) => s.replace(`{${k}}`, v), val);
    }
    return val || key;
  }, [lang]);

  const value = useMemo(() => ({ lang, setLang: changeLang, t }), [lang, changeLang, t]);

  return (
    <I18nContext.Provider value={value}>
      {children}
    </I18nContext.Provider>
  );
}

export function useI18n() {
  const ctx = useContext(I18nContext);
  if (!ctx) throw new Error('useI18n must be within I18nProvider');
  return ctx;
}
